/**
 * Client-Side Email Service
 * Interfaces with the secure server-side Brevo API endpoints
 * Generates personalized candidate PDFs and dispatches emails with real-time feedback
 */
import { generateCertificateWithTemplate } from './pdfGenerator';
import { buildEmailData, resolveTemplate } from './emailTemplates';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Check Brevo API status from the backend
 */
export async function getBrevoStatus() {
  try {
    const res = await fetch('/api/email/status');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (error) {
    return {
      connected: false,
      configured: false,
      message: error.message || 'Could not connect to backend email server.',
    };
  }
}

/**
 * Pre-send validation for a candidate
 */
export function validateCandidateForEmail(candidate) {
  if (!candidate) {
    return { valid: false, reason: 'Candidate data is missing.' };
  }
  if (!candidate.name || !candidate.name.trim()) {
    return { valid: false, reason: 'Candidate name is required.' };
  }
  const email = (candidate.email || '').trim();
  if (!email || !EMAIL_REGEX.test(email)) {
    return { valid: false, reason: `Invalid email address: "${candidate.email || ''}"` };
  }
  const credId = candidate.generatedId || candidate.verificationId;
  if (!credId) {
    return { valid: false, reason: 'Candidate document ID has not been generated yet.' };
  }

  return { valid: true };
}

/**
 * Generate candidate PDF as clean base64 string
 * Strictly ensures the PDF is generated specifically for THIS candidate
 */
export async function generateCandidatePdfBase64(candidate, settings, mode, templateUrl) {
  const doc = await generateCertificateWithTemplate(
    candidate,
    templateUrl || '/template.png',
    settings,
    mode
  );

  // Get Data URI format: "data:application/pdf;filename=generated.pdf;base64,JVBERi0xLj..."
  const dataUri = doc.output('datauristring');
  // Strip prefix to extract pure base64
  const base64 = dataUri.split(',')[1] || '';

  const cleanName = (candidate.name || 'Candidate').replace(/[^a-zA-Z0-9_-]/g, '_');
  const cleanId = (candidate.generatedId || candidate.verificationId || 'DOC').replace(/[^a-zA-Z0-9_-]/g, '_');
  const docTypeLabel = mode === 'internship' ? 'Offer_Letter' : 'Certificate';
  const fileName = `${cleanName}_${cleanId}_${docTypeLabel}.pdf`;

  return {
    base64,
    fileName,
  };
}

/**
 * Send an individual candidate email via Brevo backend
 */
export async function sendSingleCandidateEmail({
  candidate,
  settings,
  mode,
  templateUrl,
  customSubject,
  customBody,
  attachPDF = true,
}) {
  // 1. Pre-validation
  const validation = validateCandidateForEmail(candidate);
  if (!validation.valid) {
    throw new Error(validation.reason);
  }

  // 2. Personalize subject and body
  const emailData = buildEmailData(candidate, settings, mode);
  const defaultSubject = settings.emailSubject || (mode === 'internship'
    ? 'Your Internship Offer Letter \u2014 Innovation Hacks'
    : '\ud83c\udfc6 Certificate of Completion \u2013 {{candidate_name}} \u2013 {{id}}');
  const defaultBody = settings.emailBody || (mode === 'internship'
    ? `Dear {{candidate_name}},\n\nCongratulations! \ud83c\udf89 You have been selected for the Innovation Hacks Full Stack Development Internship.\n\n\ud83d\udcce Download your Official QR-Verified Offer Letter attached to this email.\n\nIntern Name: {{candidate_name}}\nIntern ID: {{internship_id}}\nInternship Duration: {{duration}}\n\nAfter downloading:\n\n1. Convert the Offer Letter into an image.\n2. Post it on LinkedIn.\n3. Tag Innovation Hacks in your post.\n\n\ud83d\ude80 After posting, stay active in the official communication channels to receive your task details and internship updates.\n\nWelcome to the Innovation Hacks Family! \u2764\ufe0f\n\nBuild. Innovate. Impact. \ud83c\udf0d\n\nBest Regards,\nKarthickkumar S\nFounder & CEO,\nInnovation Hacks\nwww.innovationhacks.in`
    : 'Dear {{candidate_name}},\n\nCongratulations! Your certificate is attached.\n\nBest Regards,\nKarthickkumar S\nFounder & CEO, Innovation Hacks');

  const resolvedSubject = customSubject ? resolveTemplate(customSubject, emailData) : resolveTemplate(defaultSubject, emailData);
  const resolvedBody = customBody ? resolveTemplate(customBody, emailData) : resolveTemplate(defaultBody, emailData);

  // 3. Generate candidate-specific PDF attachment
  let attachment = null;
  if (attachPDF) {
    const { base64, fileName } = await generateCandidatePdfBase64(candidate, settings, mode, templateUrl);
    attachment = {
      name: fileName,
      content: base64,
    };
  }

  // 4. Send request to secure server endpoint
  const response = await fetch('/api/email/send', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      candidateId: candidate.id,
      documentId: candidate.generatedId || candidate.verificationId,
      recipient: candidate.email.trim(),
      name: candidate.name.trim(),
      subject: resolvedSubject,
      body: resolvedBody,
      attachment,
    }),
  });

  const resData = await response.json();

  if (!response.ok || !resData.success) {
    const errorMsg = resData.error || `Server responded with status ${response.status}`;
    const err = new Error(errorMsg);
    err.code = resData.code;
    throw err;
  }

  return {
    success: true,
    messageId: resData.messageId,
    candidateId: candidate.id,
    sentAt: resData.sentAt || new Date().toISOString(),
    subject: resolvedSubject,
    body: resolvedBody,
    recipient: candidate.email.trim(),
  };
}

/**
 * Bulk Email Queue Processor
 * Safely iterates through candidates, generates individual PDFs, and sends via backend
 *
 * @param {Object} options
 * @param {Array} options.candidates - List of candidates to send to
 * @param {Object} options.settings - Platform settings
 * @param {string} options.mode - 'internship' | 'certificate'
 * @param {string} [options.templateUrl] - Template URL/data
 * @param {Function} [options.onProgress] - Callback with { total, sent, failed, sending, pending, current, results }
 * @param {Object} [options.cancellationToken] - { isCancelled: false }
 */
export async function sendBulkCandidateEmails({
  candidates = [],
  settings,
  mode,
  templateUrl,
  onProgress = () => {},
  cancellationToken = { isCancelled: false },
}) {
  const total = candidates.length;
  let sentCount = 0;
  let failedCount = 0;
  const results = [];

  const updateState = (currentCandidate, sendingIndex) => {
    onProgress({
      total,
      sent: sentCount,
      failed: failedCount,
      sending: currentCandidate ? 1 : 0,
      pending: Math.max(0, total - (sentCount + failedCount + (currentCandidate ? 1 : 0))),
      currentCandidate,
      currentIndex: sendingIndex,
      results: [...results],
    });
  };

  for (let i = 0; i < candidates.length; i++) {
    if (cancellationToken.isCancelled) {
      break;
    }

    const candidate = candidates[i];
    updateState(candidate, i);

    try {
      const result = await sendSingleCandidateEmail({
        candidate,
        settings,
        mode,
        templateUrl,
        attachPDF: true,
      });

      sentCount++;
      results.push({
        candidateId: candidate.id,
        candidateName: candidate.name,
        recipient: candidate.email,
        documentId: candidate.generatedId || candidate.verificationId,
        status: 'sent',
        messageId: result.messageId,
        subject: result.subject,
        body: result.body,
        sentAt: result.sentAt,
      });
    } catch (error) {
      failedCount++;
      results.push({
        candidateId: candidate.id,
        candidateName: candidate.name,
        recipient: candidate.email,
        documentId: candidate.generatedId || candidate.verificationId,
        status: 'failed',
        errorMessage: error.message || 'Failed to send email',
        errorCode: error.code || 'SEND_ERROR',
        sentAt: new Date().toISOString(),
      });
    }

    updateState(null, i);

    // Pause briefly between candidates to ensure client responsiveness and prevent rate spikes
    if (i < candidates.length - 1 && !cancellationToken.isCancelled) {
      await new Promise(r => setTimeout(r, 200));
    }
  }

  // ── FINAL PROGRESS EMISSION ──────────────────────────────────────────────
  // Fire one last onProgress with the complete results array so callers can
  // reconcile any result that was not processed during intermediate callbacks.
  // This is the definitive, authoritative source-of-truth pass: every result
  // in this array came from a confirmed Brevo provider response (success)
  // or a caught exception (failure). No result is fabricated.
  onProgress({
    total,
    sent: sentCount,
    failed: failedCount,
    sending: 0,
    pending: 0,
    currentCandidate: null,
    currentIndex: candidates.length,
    results: [...results],
    isFinal: true,   // callers can key on this to distinguish the definitive pass
  });

  return {
    total,
    sent: sentCount,
    failed: failedCount,
    results,
    cancelled: cancellationToken.isCancelled,
  };
}

/**
 * Send test email
 */
export async function sendTestEmail(recipient, name) {
  const response = await fetch('/api/email/test', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ recipient, name }),
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to send test email.');
  }

  return data;
}
